import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, TextField } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';

import { useCreateBundleItemOption } from '~/hooks/apis/cruises/useBundleItemOptionMutations';

type FormValues = {
  leOfferId: string;
  checkIn: string | null;
  checkOut: string | null;
};

const INITIAL_FORM_STATE: FormValues = {
  leOfferId: '',
  checkIn: null,
  checkOut: null,
};

type Props = {
  bundleId: string;
  bundleItemId: string;
  onClose: () => void;
};

function BundleItemOptionForm({ bundleId, bundleItemId, onClose }: Props) {
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_STATE);
  const { enqueueSnackbar } = useSnackbar();

  const createMutation = useCreateBundleItemOption();

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleChangeDateField = useCallback((value: string, name: 'checkIn' | 'checkOut') => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    createMutation.mutate(
      {
        bundleId,
        itemId: bundleItemId,
        data: {
          leOfferId: formValues.leOfferId,
          checkIn: formValues.checkIn,
          checkOut: formValues.checkOut,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Option saved successfully', { variant: 'success' });
          onClose();
        },
        onError: () => {
          enqueueSnackbar('Error saving option', { variant: 'error' });
        },
      },
    );
  }, [formValues, enqueueSnackbar, bundleId, bundleItemId, onClose, createMutation]);

  const isSaveEnabled = useMemo(() => {
    return (
      !createMutation.isPending &&
      formValues.leOfferId.trim().length > 0 &&
      !!formValues.checkIn &&
      !!formValues.checkOut
    );
  }, [formValues, createMutation.isPending]);

  return (
    <Box>
      <Box mt={2}>
        <TextField
          required
          fullWidth
          name="leOfferId"
          label="LE Offer ID"
          onChange={handleChangeField}
          value={formValues.leOfferId}
          helperText="The Luxury Escapes hotel offer ID"
        />
      </Box>

      <Box mt={2} display="flex" gap={2}>
        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          format="DD/MM/YYYY"
          label="Check-in Date *"
          value={formValues.checkIn}
          onChange={(value) => handleChangeDateField(value, 'checkIn')}
        />

        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          format="DD/MM/YYYY"
          label="Check-out Date *"
          value={formValues.checkOut}
          onChange={(value) => handleChangeDateField(value, 'checkOut')}
        />
      </Box>

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {createMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleItemOptionForm;
